/**
 * MeetingStage — the Teams/Meet-style video surface shared by BOTH Live
 * Interview rooms (RecruiterRoomPage and LiveInterviewRoomPage). It owns the
 * video grid, the presentation ("someone is sharing their screen") layout,
 * floating emoji reactions and the control bar; the pages own everything
 * around it (the question/scoring/notes/chat side panel) and drive it with
 * state from useLiveInterviewMeeting.
 *
 * Layouts:
 *  - Grid: up to GRID_PAGE_SIZE (4) tiles per page, in a 1/2/3/4-up
 *    responsive arrangement. With more participants than that, left/right
 *    arrows page through them.
 *  - Presentation: while anyone is sharing, their stream becomes the single
 *    large stage and EVERY participant (the presenter's own camera included)
 *    drops into a horizontally-scrollable thumbnail strip below it. No
 *    pagination there — the strip just scrolls.
 *
 * A participant whose camera is off renders an avatar + name instead of a
 * black <video> box.
 *
 * ---------------------------------------------------------------------------
 * THE HIDDEN RECORDER POOL (the important part — read before changing the
 * visible layout)
 * ---------------------------------------------------------------------------
 * useLiveInterviewRecorder composites the meeting by calling
 * ctx.drawImage(videoElement, …) on EVERY animation frame. That only works
 * against a <video> element that is mounted and actively decoding. This
 * component's visible tiles are none of those things reliably: pagination
 * unmounts off-page tiles, presentation mode swaps the layout wholesale, and
 * a camera-off tile renders an avatar with no <video> element at all — every
 * one of which would silently punch a hole in the recording.
 *
 * So the recorder never reads the visible tiles. Instead this component
 * keeps a separate, off-screen pool of <video> elements bound 1:1 to each
 * participant's MediaStream, mounted for as long as that participant is in
 * the call and never re-parented by any layout change, and hands THOSE to
 * the recorder via onRecorderTilesChange. The cost is decoding each stream
 * twice; the benefit is that the recording is completely decoupled from
 * whatever the pretty UI happens to be showing. Do not "optimise" this away
 * by pointing the recorder at the visible tiles.
 *
 * Documented trade-off, deliberately not solved: the local participant's
 * pool entry is always their CAMERA stream, so if they present, the
 * recording keeps capturing their camera rather than the shared screen.
 *
 * PINNED CONTROL BAR (RecruiterRoomPage only): RecruiterRoomPage now wraps
 * this component's video content in a resizable split-pane "Video" region
 * (see RecruiterRoomPage's SplitPane-based workspace and its `Pane`
 * wrapper) rather than a privileged fixed backdrop. The control bar
 * (mic/camera/share/leave/etc.) must NOT be confined to that pane's box: it
 * stays pinned to the bottom of the whole screen, reachable regardless of
 * how the Video pane is resized, mirroring Zoom/Teams. `pinnedControlBar`
 * opts into that: the control bar is portaled to `document.body` as a
 * `position: fixed` bottom bar instead of rendering inline as this
 * component's own last flex child. A portal (rather than plain in-place
 * `fixed` positioning) matters here specifically because the Video pane's
 * body renders with `overflow: hidden` (so its video content never spills
 * into neighboring panes) — a `fixed` descendant is not clipped by an
 * ancestor's `overflow: hidden` per the CSS spec, but portaling to
 * `document.body` sidesteps having to rely on that subtlety, and also
 * guarantees the bar always paints above every pane regardless of any
 * future stacking-context changes elsewhere in the workspace. Skipped while
 * fullscreen (see `isFullscreen` below) so the Fullscreen API doesn't hide
 * the controls — a portaled node lives outside `containerRef`'s subtree,
 * and the browser only renders descendants of the fullscreened element
 * while fullscreen is active. LiveInterviewRoomPage (the candidate room)
 * never sets this prop, so its behavior — control bar as a normal flex
 * child, no portal — is completely unchanged.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import {
  MicrophoneIcon, VideoCameraIcon, VideoCameraSlashIcon, ComputerDesktopIcon, FaceSmileIcon,
  ChatBubbleLeftRightIcon, ArrowsPointingOutIcon, ArrowsPointingInIcon, PhoneXMarkIcon,
  ChevronLeftIcon, ChevronRightIcon, ChevronUpIcon, StopCircleIcon, SparklesIcon,
} from '@heroicons/react/24/outline'
// Heroicons ships exactly one flat WifiIcon — no graded variants — so the
// signal-strength indicator below borrows lucide-react's wifi family, which
// draws visibly fewer arcs as the signal drops. lucide is already a
// dependency of this app (14 other files import it); nothing new was added.
import { Wifi, WifiHigh, WifiLow, WifiZero, WifiOff } from 'lucide-react'

import type {
  LiveInterviewConnectionState, LiveInterviewNetworkQuality, RemoteTile,
} from '@/hooks/useLiveInterviewCall'
import type { RecorderTile } from '@/hooks/useLiveInterviewRecorder'
import { REACTION_EMOJIS, type FloatingReaction, type PeerMediaState } from '@/hooks/useLiveInterviewMeeting'

/** Tiles shown per page before pagination kicks in. */
export const GRID_PAGE_SIZE = 4

export interface MeetingStageProps {
  localStream: MediaStream | null
  localName: string
  /** Local screen-capture stream while presenting (for the own-screen preview). */
  screenStream: MediaStream | null
  remoteTiles: RemoteTile[]
  /** Display name for a remote peer — pages label recruiters/candidates differently. */
  remoteName: (peerId: string, index: number) => string
  peerStates: Record<string, PeerMediaState>
  /** 'local' | remote peer id | null. */
  presenterId: string | null

  micOn: boolean
  cameraOn: boolean
  sharing: boolean
  screenShareSupported: boolean
  onToggleMic: () => void
  onToggleCamera: () => void
  onToggleScreenShare: () => void

  reactions: FloatingReaction[]
  onSendReaction: (emoji: string) => void

  chatOpen: boolean
  onToggleChat: () => void
  /** Unread chat messages — badges the chat control even while the side
   * panel (and its own Chat tab) is collapsed/off screen. */
  chatUnreadCount?: number

  /**
   * Disconnects THIS participant only — the meeting carries on for everyone
   * else. Always present, for every participant including the candidate.
   */
  onLeave: () => void
  leaveLabel?: string
  leaveBusy?: boolean
  /**
   * Completes the session for EVERY participant. Recruiter-only and
   * irreversible.
   *
   * It and `onLeave` share ONE control in the bar — a hang-up button with a
   * caret that opens a small menu listing both, each with a line of subtext
   * spelling out the difference ("Just you leave…" vs "Ends the meeting for
   * everyone"). Merging the two buttons is presentation only: picking an item
   * calls exactly the same callback the separate button used to, and the two
   * actions stay as distinct as they ever were — a recruiter never lands on
   * "end the interview for everybody" by aiming at "leave". Omit this (as the
   * candidate room does) and there is nothing to merge: the control collapses
   * back to a plain "Leave" button with no menu at all.
   */
  onEndInterview?: () => void
  endInterviewBusy?: boolean

  /**
   * Worst-peer WebRTC media quality, from useLiveInterviewCall's stats
   * sampler. Drives how many bars the wifi glyph in the control bar shows.
   */
  networkQuality?: LiveInterviewNetworkQuality
  /**
   * Whether the SIGNALING connection is up, retrying, or has given up.
   * Deliberately separate from `networkQuality`: that only describes the peer
   * connections while signaling is up, and says nothing about whether this
   * client can still hear the room at all. When this is anything but
   * 'connected' it overrides the quality glyph and puts a state label under it.
   */
  connectionState?: LiveInterviewConnectionState

  /** True once getUserMedia has failed — shown in place of the local tile. */
  mediaError?: boolean

  /**
   * AI Interviewer mode only: renders one extra synthetic participant tile —
   * no camera stream (there is no 3D avatar yet), just a distinct AI-branded
   * avatar with a speaking pulse — so the candidate visually sees "someone
   * named the AI interviewer joined the call", the same as any other
   * participant, rather than a call that outwardly looks empty except for a
   * text caption. Omitted entirely (as RecruiterRoomPage and human-mode
   * sessions do) renders no such tile.
   */
  aiParticipant?: { name: string; speaking: boolean; state?: AiPresenceState; statusText?: string | null } | null
  /** Fed the off-screen recorder pool; see this file's header. */
  onRecorderTilesChange?: (tiles: RecorderTile[]) => void

  /**
   * Recruiter-controlled whole-meeting recording (see useLiveInterviewRecorder
   * — it runs on the recruiter's side in this app). Omitting `onToggleRecording`
   * (as the candidate room does) hides the control entirely, the same way
   * `onEndInterview` being absent removes the "end for everyone" option — a
   * candidate never gets a button that could start/stop the recording.
   */
  isRecording?: boolean
  onToggleRecording?: () => void

  /**
   * Pins the control bar to the bottom of the viewport (via a
   * document.body portal) instead of rendering it as this component's own
   * trailing flex child — see this file's header. Used only by
   * RecruiterRoomPage now that the video content is wrapped in a
   * draggable/resizable FloatingPanel; omitted (default false) by
   * LiveInterviewRoomPage, whose control bar keeps its original inline
   * layout untouched.
   */
  pinnedControlBar?: boolean

  /**
   * Forces the pinned control bar into view (bypassing its normal
   * hover-to-reveal behavior) — used by RecruiterRoomPage's guided tour to
   * spotlight it, since a bar translated below the viewport can't be
   * highlighted. No-op when `pinnedControlBar` isn't set (LiveInterviewRoomPage's
   * inline bar is already always visible).
   */
  forceControlBarVisible?: boolean

  className?: string
}

interface StageParticipant {
  id: string
  name: string
  stream: MediaStream | null
  /** This participant's shared screen, while they're presenting; null otherwise. */
  screenStream: MediaStream | null
  isLocal: boolean
  cameraOn: boolean
  micOn: boolean
  /** The synthetic AI Interviewer tile — see MeetingStageProps.aiParticipant. */
  isAi?: boolean
  aiSpeaking?: boolean
  aiState?: AiPresenceState
  aiStatusText?: string | null
}

/** What the AI interviewer is doing right now — drives its tile's animation,
 *  so the candidate never sees a silent, motionless screen while the AI is
 *  actually working (waiting on the model, or about to speak). */
export type AiPresenceState = 'idle' | 'listening' | 'thinking' | 'speaking'

/** Five animated bars — the AI's "voice" while it is speaking. */
export function AiVoiceBars({ className = '', barClassName = 'bg-indigo-300' }: { className?: string; barClassName?: string }) {
  return (
    <span className={`inline-flex items-center gap-[3px] h-5 ${className}`} aria-hidden>
      {[0, 150, 300, 150, 0].map((delay, i) => (
        <span
          key={i}
          className={`w-[3px] h-full rounded-full origin-center animate-ai-bar ${barClassName}`}
          style={{ animationDelay: `${delay + i * 40}ms` }}
        />
      ))}
    </span>
  )
}

/** Three bouncing dots — the AI is thinking. */
export function AiThinkingDots({ className = '', dotClassName = 'bg-indigo-300' }: { className?: string; dotClassName?: string }) {
  return (
    <span className={`inline-flex items-center gap-1 ${className}`} aria-hidden>
      {[0, 160, 320].map((delay) => (
        <span key={delay} className={`w-1.5 h-1.5 rounded-full animate-ai-dot ${dotClassName}`} style={{ animationDelay: `${delay}ms` }} />
      ))}
    </span>
  )
}

function initialsOf(name: string): string {
  return (
    name.trim().split(/\s+/).slice(0, 2).map((part) => part[0]?.toUpperCase()).join('') || '?'
  )
}

/** Tailwind has no microphone-slash icon; overlay a strike instead. */
function MicIcon({ on, className }: { on: boolean; className?: string }) {
  return (
    <span className={`relative inline-flex ${className || ''}`}>
      <MicrophoneIcon className="w-5 h-5" />
      {!on && <span className="absolute inset-0 m-auto h-[1.5px] w-[120%] -translate-x-[10%] rotate-45 bg-current" />}
    </span>
  )
}

/**
 * A <video> that binds its srcObject imperatively (React does not support
 * MediaStream as a DOM attribute) and re-binds only when the stream identity
 * actually changes — re-assigning the same stream restarts playback.
 */
function StreamVideo({
  stream, muted, className, videoRef,
}: {
  stream: MediaStream | null
  muted?: boolean
  className?: string
  videoRef?: (el: HTMLVideoElement | null) => void
}) {
  const innerRef = useRef<HTMLVideoElement | null>(null)
  // Stable ref identity (it only changes if the consumer's own callback
  // does), so React attaches it on mount/unmount instead of detaching and
  // re-attaching on every render.
  const setRef = useCallback((el: HTMLVideoElement | null) => {
    innerRef.current = el
    videoRef?.(el)
  }, [videoRef])

  useEffect(() => {
    const el = innerRef.current
    if (el && el.srcObject !== stream) el.srcObject = stream
  }, [stream])

  return <video autoPlay playsInline muted={muted} className={className} ref={setRef} />
}

function ParticipantTile({
  participant, presenting, compact,
}: {
  participant: StageParticipant
  presenting?: boolean
  compact?: boolean
}) {
  const showVideo = participant.stream !== null && participant.cameraOn
  return (
    <div className={`relative w-full h-full rounded-xl overflow-hidden ring-1 ${participant.isAi ? 'bg-gradient-to-br from-indigo-950 to-gray-900 ring-indigo-400/30' : 'bg-white ring-gray-200'} ${compact ? '' : 'shadow-lg'}`}>
      {/* Mounted whenever a stream exists, camera on or off — a <video>
          element playing a stream is what actually drives audio playback
          in the browser, not just the picture. Unmounting it while the
          camera was off (the previous "video only when cameraOn" branch)
          silently killed that participant's AUDIO too, not just their
          picture. `hidden` (display:none) instead of unmounting it keeps
          audio flowing without pulling the element out of normal flow —
          an earlier version of this used `absolute inset-0`, which broke
          the picture-in-picture corner tile's sizing (its box height comes
          from this element participating in layout, same as before this
          whole fix). Kept in normal flow so nothing about box sizing
          changes from what this looked like before either fix. */}
      {participant.stream !== null && (
        <StreamVideo
          stream={participant.stream}
          muted={participant.isLocal}
          className={`w-full h-full object-cover ${showVideo ? '' : 'hidden'}`}
        />
      )}
      {!showVideo && participant.isAi ? (
        // No 3D avatar yet — a distinct AI-branded orb stands in for it. Its
        // animation always says what the AI is doing (listening / thinking /
        // speaking), so the candidate never mistakes model latency for the
        // interview having stalled.
        (() => {
          const state: AiPresenceState = participant.aiSpeaking ? 'speaking' : participant.aiState || 'idle'
          const label = state === 'speaking' ? 'Speaking'
            : state === 'thinking' ? 'Thinking'
            : state === 'listening' ? 'Listening'
            : 'AI Interviewer'
          return (
            <div className="w-full h-full flex flex-col items-center justify-center px-3">
              <div className="relative flex items-center justify-center flex-shrink-0">
                {state === 'speaking' && (
                  <>
                    <span className="absolute inset-0 rounded-full bg-indigo-400/40 animate-ping" />
                    <span className="absolute -inset-3 rounded-full bg-indigo-400/10 animate-pulse" />
                  </>
                )}
                {state === 'thinking' && (
                  // Orbiting gradient ring — continuous motion that reads as "working".
                  <span
                    className={`absolute rounded-full animate-ai-orbit ${compact ? '-inset-1' : '-inset-2.5'}`}
                    style={{ background: 'conic-gradient(from 0deg, transparent 0deg, rgba(167,139,250,0.95) 90deg, rgba(99,102,241,0.2) 180deg, transparent 270deg)' }}
                  />
                )}
                {state === 'listening' && (
                  <span className={`absolute rounded-full border-2 border-emerald-300/70 animate-ai-breathe ${compact ? '-inset-1' : '-inset-3'}`} />
                )}
                <div
                  className={`relative rounded-full bg-gradient-to-br from-indigo-500 to-violet-600 text-white flex items-center justify-center flex-shrink-0 ring-2 ${
                    state === 'speaking' ? 'ring-indigo-300'
                      : state === 'listening' ? 'ring-emerald-300/60'
                      : 'ring-indigo-500/30'
                  } ${compact ? 'w-9 h-9' : 'w-20 h-20 sm:w-28 sm:h-28'}`}
                >
                  {state === 'speaking' && !compact ? (
                    <AiVoiceBars className="h-8 sm:h-10" barClassName="bg-white" />
                  ) : (
                    <SparklesIcon className={`${compact ? 'w-4 h-4' : 'w-9 h-9 sm:w-12 sm:h-12'} ${state === 'thinking' ? 'animate-pulse' : ''}`} />
                  )}
                </div>
              </div>
              {!compact && (
                <>
                  <p className={`flex items-center gap-2 text-xs font-medium mt-4 ${
                    state === 'listening' ? 'text-emerald-200' : state === 'idle' ? 'text-indigo-300/70' : 'text-indigo-100'
                  }`}>
                    {label}
                    {state === 'thinking' && <AiThinkingDots />}
                    {state === 'listening' && <AiThinkingDots dotClassName="bg-emerald-300" />}
                  </p>
                  {participant.aiStatusText && state !== 'idle' && (
                    <p className="text-[11px] text-indigo-300/80 mt-1 text-center max-w-[16rem]">{participant.aiStatusText}</p>
                  )}
                </>
              )}
            </div>
          )
        })()
      ) : !showVideo && (
        <div className="w-full h-full flex flex-col items-center justify-center bg-white">
          <div
            className={`rounded-full bg-gray-200 text-gray-700 font-semibold flex items-center justify-center flex-shrink-0 ${
              compact ? 'w-9 h-9 text-xs' : 'w-20 h-20 sm:w-28 sm:h-28 text-2xl sm:text-3xl'
            }`}
          >
            {initialsOf(participant.name)}
          </div>
          {!compact && participant.stream === null && (
            <p className="text-xs text-gray-400 mt-2">Connecting…</p>
          )}
        </div>
      )}

      <div className="absolute bottom-1.5 left-1.5 right-1.5 flex items-center gap-1.5 pointer-events-none">
        <span className={`px-2 py-0.5 rounded-full bg-black/60 truncate ${participant.isAi ? 'text-indigo-100' : 'text-gray-100'} ${compact ? 'text-[10px]' : 'text-xs'}`}>
          {participant.name}
          {presenting ? ' · presenting' : ''}
        </span>
        {!participant.isAi && !participant.micOn && (
          <span className="flex-shrink-0 p-1 rounded-full bg-black/60 text-red-300">
            <MicIcon on={false} className="scale-[0.7]" />
          </span>
        )}
      </div>
    </div>
  )
}

/**
 * Teams/FaceTime-style picture-in-picture for the common 2-person call (one
 * interviewer, one candidate): the other participant fills the stage, your
 * own tile floats small in the corner. Click the small tile to swap which
 * side is which — same interaction Teams uses for its self-view. Only used
 * for exactly 2 participants; 3+ (multiple recruiter seats) keeps the grid
 * below, where a single "big" and "small" tile stops making sense.
 */
function PiPStage({ participants }: { participants: StageParticipant[] }) {
  const [swapped, setSwapped] = useState(false)
  const local = participants.find((p) => p.isLocal) || participants[0]
  const remote = participants.find((p) => !p.isLocal) || participants[1]
  const main = swapped ? local : remote
  const pip = swapped ? remote : local
  return (
    <div className="relative w-full h-full">
      <div className="w-full h-full">
        <ParticipantTile participant={main} />
      </div>
      <button
        type="button"
        onClick={() => setSwapped((v) => !v)}
        title="Swap views"
        aria-label="Swap main and small video"
        className="absolute bottom-3 right-3 w-24 sm:w-32 md:w-40 aspect-video rounded-lg overflow-hidden ring-2 ring-white/30 shadow-xl hover:ring-white/60 transition-all"
      >
        <ParticipantTile participant={pip} compact />
      </button>
    </div>
  )
}

/**
 * The face-cam counterpart of PiPStage, for while someone is SHARING THEIR
 * SCREEN in a 2-person call: the shared screen already fills the stage (see
 * the `presenting` branch below), so this only renders the floating corner
 * tiles — BOTH participants' camera feeds, since screen sharing sends the
 * camera as a genuine second track (see useLiveInterviewCall's file header)
 * rather than replacing it, so both faces are always available regardless
 * of who is presenting.
 *
 * Symmetric on purpose: every participant always sees the OTHER person's
 * face plus their own self-view, whether or not they themselves are the one
 * presenting — same as Teams' "people" strip staying up during a share. An
 * earlier version of this only showed the local participant when THEY were
 * presenting (on the theory "you already know your own face"), which
 * silently dropped the other participant from the presenter's own screen —
 * the presenter needs to see the OTHER person's reactions at least as much
 * as anyone else does.
 */
function PresentationFaceCam({ participants }: { participants: StageParticipant[] }) {
  const local = participants.find((p) => p.isLocal) || participants[0]
  const remote = participants.find((p) => !p.isLocal) || participants[1]
  const tiles = [remote, local]
  return (
    <div className="absolute bottom-3 right-3 flex flex-row items-end gap-2 z-10">
      {tiles.map((participant) => (
        <div
          key={participant.id}
          className="w-24 sm:w-32 md:w-40 aspect-video rounded-lg overflow-hidden ring-2 ring-white/30 shadow-xl"
        >
          <ParticipantTile participant={participant} compact />
        </div>
      ))}
    </div>
  )
}

export default function MeetingStage({
  localStream, localName, screenStream, remoteTiles, remoteName, peerStates, presenterId,
  micOn, cameraOn, sharing, screenShareSupported, onToggleMic, onToggleCamera, onToggleScreenShare,
  reactions, onSendReaction, chatOpen, onToggleChat, chatUnreadCount, onLeave, leaveLabel = 'Leave', leaveBusy,
  onEndInterview, endInterviewBusy,
  networkQuality = 'unknown', connectionState = 'connected',
  mediaError, onRecorderTilesChange, isRecording, onToggleRecording, pinnedControlBar, forceControlBarVisible, className,
  aiParticipant,
}: MeetingStageProps) {
  const [page, setPage] = useState(0)
  const [isFullscreen, setIsFullscreen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  // --- Participants ---------------------------------------------------
  const participants: StageParticipant[] = useMemo(() => {
    const list: StageParticipant[] = [{
      id: 'local',
      name: `${localName} (you)`,
      stream: localStream,
      screenStream: sharing ? screenStream : null,
      isLocal: true,
      cameraOn: cameraOn && !!localStream,
      micOn,
    }]
    remoteTiles.forEach((tile, index) => {
      const state = peerStates[tile.peerId]
      list.push({
        id: tile.peerId,
        name: remoteName(tile.peerId, index),
        stream: tile.stream,
        screenStream: tile.screenStream,
        isLocal: false,
        // Absent state = a peer that hasn't announced yet; assume their
        // camera is on rather than flashing an avatar over a live stream.
        cameraOn: state ? state.cameraOn : true,
        micOn: state ? state.micOn : true,
      })
    })
    if (aiParticipant) {
      list.push({
        id: 'ai-interviewer',
        name: aiParticipant.name,
        stream: null,
        screenStream: null,
        isLocal: false,
        cameraOn: false,
        micOn: true,
        isAi: true,
        aiSpeaking: aiParticipant.speaking,
        aiState: aiParticipant.state,
        aiStatusText: aiParticipant.statusText,
      })
    }
    return list
  }, [localName, localStream, screenStream, sharing, cameraOn, micOn, remoteTiles, peerStates, remoteName, aiParticipant])

  // --- Off-screen recorder pool (see file header) -----------------------
  const poolRefs = useRef<Record<string, HTMLVideoElement | null>>({})
  // A presenter's recorder-pool entry mounted a second, off-screen <video>
  // for their CAMERA (keyed the same as poolRefs, by participant id) — see
  // useLiveInterviewRecorder's drawFrame. Absent for anyone not presenting.
  const poolOverlayRefs = useRef<Record<string, HTMLVideoElement | null>>({})
  // Keyed by participant id, not by index, so the element bound to a given
  // participant's stream survives every reordering the visible UI does.
  const poolEntries = useMemo(() => {
    return participants
      .map((p) => {
        const isPresentingWithScreen = p.id === presenterId && !!p.screenStream
        return {
          id: p.id,
          // While presenting, the RECORDING shows the SCREEN as the main
          // stage with every participant's camera in a strip below it.
          stream: isPresentingWithScreen ? p.screenStream : p.stream,
          overlayStream: isPresentingWithScreen ? p.stream : null,
        }
      })
      .filter((e): e is { id: string; stream: MediaStream; overlayStream: MediaStream | null } => e.stream !== null)
  }, [participants, presenterId])
  const poolSignature = poolEntries.map((e) => `${e.id}:${e.overlayStream ? 1 : 0}`).join('|')
  const poolEntriesRef = useRef(poolEntries)
  poolEntriesRef.current = poolEntries
  const onRecorderTilesChangeRef = useRef(onRecorderTilesChange)
  onRecorderTilesChangeRef.current = onRecorderTilesChange

  // Recomputes the recorder's tile list and hands it up, coalesced onto a
  // microtask. Coalescing matters because React re-invokes a <video>'s ref
  // with null and then the element again on re-render; running immediately
  // would briefly report a pool with holes in it. By the time the microtask
  // drains, every ref has been re-attached.
  const notifyPendingRef = useRef(false)
  const scheduleRecorderNotify = useCallback(() => {
    if (notifyPendingRef.current) return
    notifyPendingRef.current = true
    queueMicrotask(() => {
      notifyPendingRef.current = false
      const notify = onRecorderTilesChangeRef.current
      if (!notify) return
      const tiles: RecorderTile[] = []
      poolEntriesRef.current.forEach(({ id, stream, overlayStream }) => {
        const video = poolRefs.current[id]
        if (!video) return
        const overlayVideo = overlayStream ? poolOverlayRefs.current[id] || undefined : undefined
        tiles.push({ video, stream, overlayVideo, overlayStream: overlayStream || undefined })
      })
      notify(tiles)
    })
  }, [])

  // One STABLE ref callback per participant id, memoised in a ref. A fresh
  // inline arrow here would be a new ref identity every render, which React
  // detaches and re-attaches every render — harmless on its own, but it
  // makes it far too easy to hang a setState off the attach and spin the
  // component forever. Stable callbacks fire on genuine mount/unmount only.
  const poolRefSettersRef = useRef<Record<string, (el: HTMLVideoElement | null) => void>>({})
  const getPoolRefSetter = (id: string) => {
    if (!poolRefSettersRef.current[id]) {
      poolRefSettersRef.current[id] = (el: HTMLVideoElement | null) => {
        poolRefs.current[id] = el
        scheduleRecorderNotify()
      }
    }
    return poolRefSettersRef.current[id]
  }

  const poolOverlayRefSettersRef = useRef<Record<string, (el: HTMLVideoElement | null) => void>>({})
  const getPoolOverlayRefSetter = (id: string) => {
    if (!poolOverlayRefSettersRef.current[id]) {
      poolOverlayRefSettersRef.current[id] = (el: HTMLVideoElement | null) => {
        poolOverlayRefs.current[id] = el
        scheduleRecorderNotify()
      }
    }
    return poolOverlayRefSettersRef.current[id]
  }

  // Covers the case where the pool membership changed but the <video>
  // elements themselves did not remount (e.g. a peer's stream was replaced
  // in place), which no ref callback would fire for.
  useEffect(() => {
    // Drop bookkeeping for participants that have left, so neither map grows
    // for the lifetime of a long call.
    const live = new Set(poolEntriesRef.current.map((e) => e.id))
    Object.keys(poolRefs.current).forEach((id) => {
      if (!live.has(id)) {
        delete poolRefs.current[id]
        delete poolRefSettersRef.current[id]
      }
    })
    Object.keys(poolOverlayRefs.current).forEach((id) => {
      if (!live.has(id)) {
        delete poolOverlayRefs.current[id]
        delete poolOverlayRefSettersRef.current[id]
      }
    })
    scheduleRecorderNotify()
  }, [poolSignature, scheduleRecorderNotify])

  // --- Pagination -------------------------------------------------------
  const presenting = presenterId !== null
  const pageCount = Math.max(1, Math.ceil(participants.length / GRID_PAGE_SIZE))
  useEffect(() => {
    if (page > pageCount - 1) setPage(pageCount - 1)
  }, [page, pageCount])
  const pageParticipants = participants.slice(page * GRID_PAGE_SIZE, page * GRID_PAGE_SIZE + GRID_PAGE_SIZE)

  // --- Fullscreen -------------------------------------------------------
  useEffect(() => {
    const onChange = () => setIsFullscreen(document.fullscreenElement === containerRef.current)
    document.addEventListener('fullscreenchange', onChange)
    return () => document.removeEventListener('fullscreenchange', onChange)
  }, [])

  const toggleFullscreen = useCallback(() => {
    const element = containerRef.current
    if (!element) return
    if (document.fullscreenElement) {
      void document.exitFullscreen().catch(() => {})
    } else {
      void element.requestFullscreen?.().catch(() => {})
    }
  }, [])

  // --- Presentation stage ----------------------------------------------
  const presenterParticipant = presenterId ? participants.find((p) => p.id === presenterId) : undefined
  // Presenting locally: preview our OWN capture (the same track the outgoing
  // SECOND sender carries to everyone else — see useLiveInterviewCall's file
  // header). Presenting remotely: the presenter's own screenStream field,
  // never their camera `.stream` — screen sharing is a genuine second track
  // now, so the two are always distinct.
  const stageStream = presenterId === 'local' ? screenStream : (presenterParticipant ? presenterParticipant.screenStream : null)

  const gridClass =
    pageParticipants.length <= 1 ? 'grid-cols-1'
      : pageParticipants.length === 2 ? 'grid-cols-1 sm:grid-cols-2'
        : 'grid-cols-2'

  return (
    <div
      ref={containerRef}
      className={`relative flex flex-col min-h-0 bg-white ${className || ''}`}
    >
      {/* Float-and-fade for reactions. Scoped to this component rather than
          added to the Tailwind config — it is the only thing that uses it. */}
      <style>{`
        @keyframes liveInterviewReactionFloat {
          0%   { opacity: 0; transform: translateY(8px) scale(0.8); }
          15%  { opacity: 1; transform: translateY(0) scale(1); }
          100% { opacity: 0; transform: translateY(-140px) scale(1.25); }
        }
      `}</style>

      <div className="relative flex-1 min-h-0 p-3">
        {mediaError && (
          <div className="absolute top-3 left-1/2 -translate-x-1/2 z-20 px-3 py-1.5 rounded-full bg-red-600/90 text-white text-xs">
            Camera/microphone access denied — others can still see and hear each other.
          </div>
        )}

        {presenting ? (
          <div className="relative h-full min-h-0 rounded-xl bg-white overflow-hidden ring-1 ring-gray-200">
            {stageStream ? (
              <StreamVideo
                stream={stageStream}
                muted={presenterId === 'local'}
                className="w-full h-full object-contain bg-white"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-gray-400 text-sm">
                Waiting for the shared screen…
              </div>
            )}
            <span className="absolute top-2 left-2 text-[11px] px-2 py-0.5 rounded-full bg-black/70 text-gray-100">
              {presenterParticipant ? `${presenterParticipant.name} is presenting` : 'Presenting'}
            </span>

            {/* The common 2-person call: the shared screen fills the whole
                stage, and a face-cam PiP (defaulting to the OTHER
                participant) floats in the corner so presenting doesn't make
                them disappear. 3+ participants (multiple recruiter seats)
                falls back to a scrollable thumbnail strip — no single
                "other person" to default the corner to. */}
            {participants.length === 2 ? (
              <PresentationFaceCam participants={participants} />
            ) : (
              <div className="absolute bottom-2 left-2 right-2 flex gap-2 overflow-x-auto pb-1">
                {participants.map((participant) => (
                  <div key={participant.id} className="w-32 flex-shrink-0 aspect-video">
                    <ParticipantTile
                      participant={participant}
                      compact
                      presenting={participant.id === presenterId}
                    />
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : (
          <div className="relative h-full min-h-0 flex items-center">
            {pageCount > 1 && (
              <button
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                disabled={page === 0}
                aria-label="Previous participants"
                className="absolute left-0 z-10 p-1.5 rounded-full bg-black/60 text-white hover:bg-black/80 disabled:opacity-30"
              >
                <ChevronLeftIcon className="w-5 h-5" />
              </button>
            )}

            {pageParticipants.length === 2 ? (
              <PiPStage participants={pageParticipants} />
            ) : (
              <div className={`grid gap-2 w-full h-full ${gridClass} ${pageParticipants.length > 2 ? 'grid-rows-2' : ''} ${pageCount > 1 ? 'px-9' : ''}`}>
                {pageParticipants.map((participant, idx) => (
                  <div
                    key={participant.id}
                    className={`min-h-0 ${pageParticipants.length === 3 && idx === 2 ? 'col-span-2' : ''}`}
                  >
                    <ParticipantTile participant={participant} />
                  </div>
                ))}
              </div>
            )}

            {pageCount > 1 && (
              <button
                onClick={() => setPage((p) => Math.min(pageCount - 1, p + 1))}
                disabled={page >= pageCount - 1}
                aria-label="Next participants"
                className="absolute right-0 z-10 p-1.5 rounded-full bg-black/60 text-white hover:bg-black/80 disabled:opacity-30"
              >
                <ChevronRightIcon className="w-5 h-5" />
              </button>
            )}

            {pageCount > 1 && (
              <span className="absolute bottom-1 left-1/2 -translate-x-1/2 text-[10px] text-gray-400">
                {page + 1} / {pageCount}
              </span>
            )}
          </div>
        )}

        {/* Floating reactions — purely visual, never persisted. */}
        <div className="pointer-events-none absolute inset-0 overflow-hidden">
          {reactions.map((reaction) => (
            <span
              key={reaction.id}
              className="absolute bottom-6 text-3xl select-none"
              style={{
                left: `${reaction.offsetPercent}%`,
                animation: 'liveInterviewReactionFloat 2.5s ease-out forwards',
              }}
            >
              {reaction.emoji}
            </span>
          ))}
        </div>
      </div>

      {(() => {
        // See this file's header ("PINNED CONTROL BAR") for why a pinned bar
        // is portaled to document.body rather than just given
        // `position: fixed` in place, and why it falls back to the normal
        // inline render while fullscreen.
        const controlBar = (
          <MeetingControlBar
            micOn={micOn}
            onToggleMic={onToggleMic}
            cameraOn={cameraOn}
            onToggleCamera={onToggleCamera}
            sharing={sharing}
            screenShareSupported={screenShareSupported}
            onToggleScreenShare={onToggleScreenShare}
            onSendReaction={onSendReaction}
            chatOpen={chatOpen}
            onToggleChat={onToggleChat}
            chatUnreadCount={chatUnreadCount}
            isFullscreen={isFullscreen}
            onToggleFullscreen={toggleFullscreen}
            onLeave={onLeave}
            leaveLabel={leaveLabel}
            leaveBusy={leaveBusy}
            onEndInterview={onEndInterview}
            endInterviewBusy={endInterviewBusy}
            networkQuality={networkQuality}
            connectionState={connectionState}
            isRecording={isRecording}
            onToggleRecording={onToggleRecording}
          />
        )
        if (pinnedControlBar && !isFullscreen) {
          return <AutoHidingPinnedControlBar forceVisible={forceControlBarVisible}>{controlBar}</AutoHidingPinnedControlBar>
        }
        return controlBar
      })()}

      {/*
        Off-screen recorder pool — NOT part of the visible layout. One
        <video> per participant stream (plus a second, overlay <video> for
        whoever is presenting — see this component's screen-share/camera
        compositing notes above), mounted for as long as that participant is
        in the call, never touched by pagination / presentation / camera-off.
        See this file's header for why this cannot be folded into the tiles
        above.
      */}
      <div
        aria-hidden
        className="pointer-events-none fixed left-0 top-0 -z-10 opacity-0"
        style={{ width: 1, height: 1, overflow: 'hidden' }}
      >
        {poolEntries.map(({ id, stream, overlayStream }) => (
          <React.Fragment key={id}>
            <StreamVideo
              stream={stream}
              muted
              className="w-40 h-24 object-cover"
              videoRef={getPoolRefSetter(id)}
            />
            {overlayStream && (
              <StreamVideo
                stream={overlayStream}
                muted
                className="w-40 h-24 object-cover"
                videoRef={getPoolOverlayRefSetter(id)}
              />
            )}
          </React.Fragment>
        ))}
      </div>
    </div>
  )
}

/**
 * Wraps the pinned control bar in a hover-to-reveal strip: hidden below the
 * bottom edge by default (so it's out of the way of the floating panels'
 * bottom row) and slides up when the cursor approaches the bottom of the
 * screen, or while hovering the bar itself — including any of its own
 * popovers (the reaction picker / leave menu render as DOM descendants of
 * `children`, so hovering them keeps this wrapper's mouseenter state; the
 * native mouseleave/mouseenter pair doesn't fire when moving onto a
 * descendant, only when truly leaving the subtree). A thin, always-present
 * hotzone at the very bottom edge catches the cursor even while the bar
 * itself is translated out of view and therefore not under the pointer.
 * Kept to h-1.5 (6px) rather than a taller strip: RecruiterRoomPage reserves
 * a small BOTTOM_RESERVE_PX clearance (8px) at the bottom of its dockview
 * workspace specifically so this always-present, `pointer-events: auto`
 * hotzone has its own pixels to live in that never overlap real panel
 * content (the Chat panel's message-input row sits right at the bottom of
 * its panel) — a taller hotzone here would eat back into that reserved
 * clearance and start intercepting clicks meant for that content again. See
 * RecruiterRoomPage's BOTTOM_RESERVE_PX comment.
 */
function AutoHidingPinnedControlBar({ children, forceVisible }: { children: React.ReactNode; forceVisible?: boolean }) {
  const [hovered, setHovered] = useState(false)
  const visible = hovered || forceVisible

  return createPortal(
    // pointer-events-none on this OUTER shell is load-bearing, not
    // decorative: `transform` (translate-y-full below) moves the control
    // bar OUT OF VIEW visually, but does NOT remove it from normal flow or
    // shrink the space it occupies — so without this, this wrapper's own
    // hit-testable box still spans the bar's full height at the bottom of
    // the screen even while the bar itself is translated away, silently
    // swallowing every click meant for whatever's rendered underneath it
    // (e.g. Chat's own message input, when Chat is docked at the bottom of
    // the workspace). Each of the two children opts back into
    // pointer-events itself, since they're the only parts ever meant to be
    // interactive.
    <div className="fixed inset-x-0 bottom-0 z-[300] pointer-events-none">
      <div
        className="absolute inset-x-0 bottom-0 h-1.5 pointer-events-auto"
        style={visible ? { pointerEvents: 'none' } : undefined}
        onMouseEnter={() => setHovered(true)}
      />
      <div
        // Guided-tour spotlight anchor (RecruiterRoomPage's RecruiterInterviewTour)
        // — only meaningful while `visible`, since this element sits below the
        // viewport (translate-y-full) otherwise and would measure as an
        // off-screen rect.
        data-tour="control-bar"
        className={`pointer-events-auto transition-transform duration-200 ease-out ${visible ? 'translate-y-0' : 'translate-y-full'}`}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
      >
        {children}
      </div>
    </div>,
    document.body
  )
}

/**
 * The mic/camera/share/reactions/chat/fullscreen/leave/recording control bar
 * — extracted out of MeetingStage's own render so it can be rendered exactly
 * once and then either portaled to document.body (pinned, RecruiterRoomPage)
 * or left as a normal flex child (LiveInterviewRoomPage) without duplicating
 * this JSX in two places. See MeetingStage's own "PINNED CONTROL BAR" header
 * comment for why the portal is needed at all.
 *
 * Owns its own `pickerOpen`/`leaveMenuOpen` popover state — both are pure
 * control-bar UI that nothing outside this component reads.
 */
function MeetingControlBar({
  micOn, onToggleMic, cameraOn, onToggleCamera, sharing, screenShareSupported, onToggleScreenShare,
  onSendReaction, chatOpen, onToggleChat, chatUnreadCount,
  isFullscreen, onToggleFullscreen,
  onLeave, leaveLabel, leaveBusy, onEndInterview, endInterviewBusy,
  networkQuality, connectionState, isRecording, onToggleRecording,
}: {
  micOn: boolean
  onToggleMic: () => void
  cameraOn: boolean
  onToggleCamera: () => void
  sharing: boolean
  screenShareSupported: boolean
  onToggleScreenShare: () => void
  onSendReaction: (emoji: string) => void
  chatOpen: boolean
  onToggleChat: () => void
  chatUnreadCount?: number
  isFullscreen: boolean
  onToggleFullscreen: () => void
  onLeave: () => void
  leaveLabel: string
  leaveBusy?: boolean
  onEndInterview?: () => void
  endInterviewBusy?: boolean
  networkQuality: LiveInterviewNetworkQuality
  connectionState: LiveInterviewConnectionState
  isRecording?: boolean
  onToggleRecording?: () => void
}) {
  const [pickerOpen, setPickerOpen] = useState(false)
  const [leaveMenuOpen, setLeaveMenuOpen] = useState(false)

  // Escape closes the leave/end menu. It sits over an irreversible action, so
  // it must be dismissible without having to aim at the backdrop — especially
  // on the mobile layout, where it is a centered modal rather than a dropdown
  // hanging off the button that opened it.
  useEffect(() => {
    if (!leaveMenuOpen) return
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setLeaveMenuOpen(false)
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [leaveMenuOpen])

  return (
    <div className="flex-shrink-0 relative flex items-center justify-center gap-2 px-3 py-2.5 bg-gray-950/80 border-t border-white/10">
      <ControlButton
        onClick={onToggleMic}
        label={micOn ? 'Mute microphone' : 'Unmute microphone'}
        danger={!micOn}
      >
        <MicIcon on={micOn} />
      </ControlButton>

      <ControlButton
        onClick={onToggleCamera}
        label={cameraOn ? 'Turn camera off' : 'Turn camera on'}
        danger={!cameraOn}
      >
        {cameraOn ? <VideoCameraIcon className="w-5 h-5" /> : <VideoCameraSlashIcon className="w-5 h-5" />}
      </ControlButton>

      <ControlButton
        onClick={onToggleScreenShare}
        disabled={!screenShareSupported}
        label={
          !screenShareSupported ? 'Screen sharing is not supported in this browser'
            : sharing ? 'Stop sharing your screen' : 'Share your screen'
        }
        highlighted={sharing}
      >
        <ComputerDesktopIcon className="w-5 h-5" />
      </ControlButton>

      <div className="relative">
        <ControlButton onClick={() => setPickerOpen((o) => !o)} label="Send a reaction" highlighted={pickerOpen}>
          <FaceSmileIcon className="w-5 h-5" />
        </ControlButton>
        {pickerOpen && (
          <div className="absolute bottom-full mb-2 left-1/2 -translate-x-1/2 flex gap-1 px-2 py-1.5 rounded-full bg-gray-800 ring-1 ring-white/10 shadow-xl">
            {REACTION_EMOJIS.map((emoji) => (
              <button
                key={emoji}
                onClick={() => { onSendReaction(emoji); setPickerOpen(false) }}
                className="text-xl leading-none p-1 rounded-full hover:bg-white/10"
                aria-label={`React with ${emoji}`}
              >
                {emoji}
              </button>
            ))}
          </div>
        )}
      </div>

      <ControlButton onClick={onToggleChat} label="Toggle chat & panel" highlighted={chatOpen} badge={chatUnreadCount}>
        <ChatBubbleLeftRightIcon className="w-5 h-5" />
      </ControlButton>

      <ControlButton onClick={onToggleFullscreen} label={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}>
        {isFullscreen ? <ArrowsPointingInIcon className="w-5 h-5" /> : <ArrowsPointingOutIcon className="w-5 h-5" />}
      </ControlButton>

      {/*
        Two DISTINCT actions behind ONE control. "Leave" only drops this
        participant (the meeting continues without them); "End Interview"
        completes the session for everyone and is recruiter-only. They are
        merged into a single hang-up button with a caret purely to stop the
        control bar carrying two near-identical red-ish hang-up buttons side
        by side — the menu spells out which is which in subtext, and each
        item still calls exactly the callback its own button used to.

        The candidate room passes no onEndInterview at all, so there is
        nothing to merge there: it falls through to the plain button below
        and never renders a menu.
      */}
      {onEndInterview ? (
        <div className="relative ml-2">
          <button
            onClick={() => setLeaveMenuOpen((o) => !o)}
            disabled={leaveBusy || endInterviewBusy}
            aria-haspopup="menu"
            aria-expanded={leaveMenuOpen}
            title="Leave or end this interview"
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-gray-700 text-white text-xs font-medium hover:bg-gray-600 disabled:opacity-50"
          >
            <PhoneXMarkIcon className="w-4 h-4" />
            {leaveBusy ? 'Leaving…' : endInterviewBusy ? 'Ending…' : leaveLabel}
            <ChevronUpIcon className={`w-3.5 h-3.5 transition-transform ${leaveMenuOpen ? '' : 'rotate-180'}`} />
          </button>

          {leaveMenuOpen && (
            <>
              {/* Transparent full-screen catcher: one tap anywhere else
                  closes the menu, including on the mobile layout where the
                  menu is nowhere near the button that opened it. */}
              <div
                className="fixed inset-0 z-40"
                aria-hidden
                onClick={() => setLeaveMenuOpen(false)}
              />
              {/*
                Responsive by the app's own default-Tailwind convention (the
                only breakpoint the live-interview UI already uses is `sm:`):
                BELOW sm this is a centered popup — a tiny dropdown anchored
                to a button in a cramped control bar is hard to tap
                accurately, and the wrong tap here ends everyone's interview.
                From sm up it is the anchored dropdown, sitting just above
                the button exactly like the reaction picker does.
              */}
              <div
                role="menu"
                className="
                  fixed left-1/2 top-1/2 z-50 w-[min(88vw,320px)] -translate-x-1/2 -translate-y-1/2
                  flex flex-col gap-0.5 rounded-xl bg-gray-800 p-1.5 shadow-2xl ring-1 ring-white/10
                  sm:absolute sm:bottom-full sm:top-auto sm:mb-2 sm:w-64 sm:translate-y-0
                "
              >
                <LeaveMenuItem
                  icon={<PhoneXMarkIcon className="w-5 h-5" />}
                  title={leaveLabel}
                  subtitle="Just you leave — the meeting continues"
                  onClick={() => { setLeaveMenuOpen(false); onLeave() }}
                  disabled={leaveBusy}
                />
                <LeaveMenuItem
                  icon={<StopCircleIcon className="w-5 h-5" />}
                  title="End Interview"
                  subtitle="Ends the meeting for everyone"
                  onClick={() => { setLeaveMenuOpen(false); onEndInterview() }}
                  disabled={endInterviewBusy}
                  danger
                />
              </div>
            </>
          )}
        </div>
      ) : (
        <button
          onClick={onLeave}
          disabled={leaveBusy}
          className="ml-2 inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-gray-700 text-white text-xs font-medium hover:bg-gray-600 disabled:opacity-50"
        >
          <PhoneXMarkIcon className="w-4 h-4" />
          {leaveBusy ? 'Leaving…' : leaveLabel}
        </button>
      )}

      {/* Network/connection state, right after the hang-up control. */}
      <NetworkIndicator quality={networkQuality} connectionState={connectionState} />

      {/* Recruiter-only — absent entirely on the candidate room (no
          onToggleRecording passed there), same "omit the prop, omit the
          control" convention onEndInterview uses above. Recording is
          explicit and recruiter-controlled, never automatic. Placed last —
          the far right corner of the bar, after Leave/End Interview.
          A labeled pill rather than an icon-only ControlButton: this is
          an important, easy-to-miss action, and an icon alone (plus a
          hover-only tooltip) wasn't clear enough at a glance. */}
      {onToggleRecording && (
        <button
          type="button"
          onClick={onToggleRecording}
          title={isRecording ? 'Stop Session Recording' : 'Start Session Recording'}
          aria-label={isRecording ? 'Stop Session Recording' : 'Start Session Recording'}
          className={`inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full text-xs font-medium transition-colors ${
            isRecording
              ? 'bg-red-600 text-white hover:bg-red-700'
              : 'bg-white/10 text-gray-100 hover:bg-white/20'
          }`}
        >
          {isRecording ? (
            <>
              <StopCircleIcon className="w-4 h-4" />
              <span className="relative flex h-2 w-2 flex-shrink-0">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-white" />
              </span>
              Stop Session Recording
            </>
          ) : (
            <>
              <span className="w-2.5 h-2.5 rounded-full bg-red-500 flex-shrink-0" />
              Start Session Recording
            </>
          )}
        </button>
      )}
    </div>
  )
}

/**
 * One row of the merged Leave / End Interview menu. The subtitle is not
 * decoration: these two actions are one tap apart and only one of them is
 * reversible, so each says plainly what it does to everyone else.
 */
function LeaveMenuItem({
  icon, title, subtitle, onClick, disabled, danger,
}: {
  icon: React.ReactNode
  title: string
  subtitle: string
  onClick: () => void
  disabled?: boolean
  danger?: boolean
}) {
  return (
    <button
      role="menuitem"
      onClick={onClick}
      disabled={disabled}
      className={`flex items-start gap-2.5 w-full text-left px-2.5 py-2.5 sm:py-2 rounded-lg transition-colors disabled:opacity-50 ${
        danger ? 'hover:bg-red-500/15' : 'hover:bg-white/10'
      }`}
    >
      <span className={`flex-shrink-0 mt-0.5 ${danger ? 'text-red-400' : 'text-gray-300'}`}>{icon}</span>
      <span className="flex flex-col min-w-0">
        <span className={`text-xs font-semibold ${danger ? 'text-red-400' : 'text-gray-100'}`}>{title}</span>
        <span className="text-[11px] leading-snug text-gray-400">{subtitle}</span>
      </span>
    </button>
  )
}

/**
 * Signal-strength indicator: a real wifi glyph whose arcs visibly thin out as
 * the link degrades, rather than a colored dot that only conveys anything if
 * you already know the legend.
 *
 * `connectionState` OUTRANKS `quality` whenever it is not 'connected'.
 * "Reconnecting" and "the peer connections are a bit lossy" are different
 * problems with different fixes, and reporting a stale "good" while the
 * signaling socket is actually down would be the more misleading of the two.
 * The text label appears ONLY in those abnormal states — during a normal call
 * the icon alone carries it, and a permanent "Good connection" caption in the
 * control bar is just noise.
 */
function NetworkIndicator({
  quality, connectionState,
}: {
  quality: LiveInterviewNetworkQuality
  connectionState: LiveInterviewConnectionState
}) {
  const view = (() => {
    if (connectionState === 'reconnecting') {
      return { Icon: WifiLow, color: 'text-amber-400', title: 'Reconnecting…', label: 'Reconnecting…', pulse: true }
    }
    if (connectionState === 'failed') {
      return { Icon: WifiOff, color: 'text-red-500', title: 'Connection lost — leaving', label: 'Leaving…', pulse: false }
    }
    if (connectionState === 'disconnected') {
      return { Icon: WifiOff, color: 'text-red-500', title: 'Connection lost', label: 'Disconnected', pulse: false }
    }
    switch (quality) {
      case 'good': return { Icon: Wifi, color: 'text-green-500', title: 'Good connection', label: null, pulse: false }
      case 'fair': return { Icon: WifiHigh, color: 'text-amber-400', title: 'Fair connection', label: null, pulse: false }
      case 'poor': return { Icon: WifiLow, color: 'text-red-500', title: 'Poor connection', label: null, pulse: false }
      default: return { Icon: WifiZero, color: 'text-gray-500', title: 'Connection quality unknown', label: null, pulse: false }
    }
  })()
  const { Icon } = view

  return (
    <div
      className="flex flex-col items-center justify-center min-w-[42px] px-1 leading-none"
      title={view.title}
      aria-live="polite"
      aria-label={view.title}
    >
      <Icon className={`w-5 h-5 ${view.color} ${view.pulse ? 'animate-pulse' : ''}`} aria-hidden />
      {view.label && (
        <span className="mt-0.5 text-[10px] text-gray-300 whitespace-nowrap">{view.label}</span>
      )}
    </div>
  )
}

function ControlButton({
  children, onClick, label, danger, highlighted, disabled, badge,
}: {
  children: React.ReactNode
  onClick: () => void
  label: string
  /** Red "this is off / destructive" styling — mic muted, camera off. */
  danger?: boolean
  /** Blue "this is currently on" styling — sharing, chat open, picker open. */
  highlighted?: boolean
  disabled?: boolean
  /** A small count badge in the corner — e.g. unread chat messages. Stays
   * visible even while the panel it opens is collapsed, which is the point:
   * the whole reason to badge THIS icon instead of only a tab inside the
   * panel is that the panel itself might not be on screen at all. */
  badge?: number
}) {
  return (
    <button
      onClick={onClick}
      title={label}
      aria-label={label}
      disabled={disabled}
      className={`relative p-2.5 rounded-full transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
        danger ? 'bg-red-600 text-white hover:bg-red-700'
          : highlighted ? 'bg-blue-600 text-white hover:bg-blue-700'
            : 'bg-white/10 text-gray-100 hover:bg-white/20'
      }`}
    >
      {children}
      {!!badge && (
        <span className="absolute -top-1 -right-1 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-semibold leading-none ring-2 ring-gray-950">
          {badge > 9 ? '9+' : badge}
        </span>
      )}
    </button>
  )
}
